import {
  buttonVariants,
  DimeCard,
  DimeCardAction,
  DimeCardDescription,
  DimeCardHeader,
  DimeCardTitle,
  DimeDataTable,
} from '@nudool/design-system';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Package } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import { ListEmptyState } from '../../components/ListEmptyState.js';
import { ListErrorState } from '../../components/ListErrorState.js';
import {
  parseListRouteSearch,
  type ListRouteSearch,
} from '../../hooks/use-list-table-helpers/index.js';
import { useListTable } from '../../hooks/use-list-table.js';
import { PermissionGuard } from '../../lib/permission-guard.js';
import { usePermissions } from '../../modules/iam/permissions-context.js';
import {
  GET_PARTS,
  type GetPartsQueryResult,
  type GetPartsQueryVariables,
  type Part,
} from '../../modules/parts/operations.js';
import { makePartColumns } from '../../modules/parts/part-columns.js';
import { PartFilterSummary } from '../../modules/parts/part-filter-summary.js';
import {
  buildPartListWhere,
  countActivePartFilters,
  emptyPartListFilters,
  type PartListFilters,
  type PartListWhere,
} from '../../modules/parts/part-list-filters.js';
import { PartListToolbarFilters } from '../../modules/parts/part-list-toolbar-filters.js';
import { usePartFilterOptions } from '../../modules/parts/use-part-filter-options.js';

// Verified against SORTABLE_FIELDS.Part; `uom`, `brands`, and `sellable` have no sortable column.
export const PART_SORT_MAP: Record<string, string[]> = {
  num: ['num'],
  name: ['name'],
  partType: ['typeId'],
  controlled: ['controlledFlag'],
  revision: ['revision'],
  stdCost: ['stdCost'],
  leadTime: ['leadTime'],
};

export const Route = createFileRoute('/parts/')({
  validateSearch: (search: Record<string, unknown>): ListRouteSearch =>
    parseListRouteSearch(search),
  component: SetupPartsPage,
});

// eslint-disable-next-line react-refresh/only-export-components
function SetupPartsPage() {
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const canEdit = permissions.includes('part:edit');
  const [filters, setFilters] = useState(emptyPartListFilters);

  const where = useMemo(() => buildPartListWhere(filters), [filters]);

  const applyFilters = useCallback((next: PartListFilters) => {
    setFilters(next);
  }, []);
  const activeFilterCount = countActivePartFilters(filters);

  const filterOptions = usePartFilterOptions();
  const { brands, partTypes, uoms, showBrand } = filterOptions;

  const columns = useMemo(
    () => makePartColumns(navigate, canEdit, showBrand),
    [navigate, canEdit, showBrand],
  );

  const {
    rows: parts,
    loading,
    error,
    refetch,
    tableProps,
    search,
  } = useListTable<GetPartsQueryResult, GetPartsQueryVariables, Part, PartListWhere>({
    query: GET_PARTS,
    extractConnection: (data) => data?.parts,
    defaultSort: [{ id: 'num', desc: false }],
    sortMap: PART_SORT_MAP,
    sortTiebreakerField: null, // PartOrderBy has no `id` field yet.
    where,
    variables: {
      canViewBrands: filterOptions.canViewBrands,
      canViewProducts: filterOptions.canViewProducts,
    },
  });

  const newPartAction = (
    <PermissionGuard
      required={['part:edit']}
      pass={
        <Link to="/parts/new" className={buttonVariants({})}>
          + New Part
        </Link>
      }
      fail={
        <span className={buttonVariants({})} role="link" aria-disabled="true">
          + New Part
        </span>
      }
    />
  );

  // Only the genuinely-empty catalogue gets the onboarding empty state. A filtered-to-nothing
  // result must not read as "you have no parts".
  const isPristineAndEmpty = !loading && parts.length === 0 && !search && activeFilterCount === 0;

  return (
    <DimeCard borderless className="gap-0 py-0">
      <DimeCardHeader className="px-0 mb-4">
        <DimeCardTitle className="text-base flex items-center gap-2">Parts</DimeCardTitle>
        <DimeCardDescription className="text-sm">
          Define and manage part records — descriptions, costs, UOM, and other master data.
        </DimeCardDescription>
        <DimeCardAction>{newPartAction}</DimeCardAction>
      </DimeCardHeader>
      {error && parts.length === 0 ? (
        <ListErrorState
          title="Could not load parts"
          message={error.message}
          onRetry={() => void refetch()}
        />
      ) : isPristineAndEmpty ? (
        <ListEmptyState
          icon={<Package />}
          title="No parts yet"
          description="Add your first part to start tracking inventory, costs, and vendor sourcing."
          action={newPartAction}
        />
      ) : (
        <DimeDataTable
          columns={columns}
          data={parts}
          maxBodyHeight="viewport"
          onRowClick={(part) =>
            navigate({
              to: '/parts/$partId',
              params: { partId: part.id },
              search: { edit: false },
            })
          }
          searchColumn="num"
          searchPlaceholder="Search by part number or name…"
          showViewOptions
          emptyMessage="No parts found. Try adjusting your search or filters."
          filters={
            <PartListToolbarFilters
              filters={filters}
              brands={brands}
              partTypes={partTypes}
              uoms={uoms}
              optionsLoading={filterOptions.optionsLoading}
              brandsUnavailable={filterOptions.brandsUnavailable}
              onChange={applyFilters}
            />
          }
          actions={
            <PartFilterSummary
              filters={filters}
              brands={brands}
              partTypes={partTypes}
              uoms={uoms}
              onChange={applyFilters}
            />
          }
          loadingRowCount={tableProps.pageSize}
          exportFileName="parts"
          {...tableProps}
        />
      )}
    </DimeCard>
  );
}
